import React, { useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/config';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV', desc: 'Spreadsheet (Excel compatible)' },
  { value: 'xlsx', label: 'Excel', desc: 'Microsoft Excel file' },
  { value: 'json', label: 'JSON', desc: 'Raw data format' },
  { value: 'pdf', label: 'PDF', desc: 'Printable document' },
];

// User-friendly filter types → maps to API type + format
const FILTER_TYPES = [
  { label: 'Date', value: 'date', type: 'string', format: 'date', icon: 'Calendar', example: 'e.g. Start Date, End Date' },
  { label: 'Text', value: 'text', type: 'string', format: '', icon: 'Type', example: 'e.g. Customer Name, Status' },
  { label: 'Number', value: 'number', type: 'number', format: '', icon: 'Hash', example: 'e.g. Min Amount, Max Amount' },
  { label: 'Yes / No', value: 'boolean', type: 'boolean', format: '', icon: 'ToggleLeft', example: 'e.g. Paid Only, Active Only' },
];

const toKey = (label) =>
  label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const REPORT_TEMPLATES = [
  { key: 'finance_summary',    name: 'Finance Summary' },
  { key: 'sales_invoices',     name: 'Sales Invoices' },
  { key: 'purchases_invoices', name: 'Purchases Invoices' },
  { key: 'expenses',           name: 'Expenses' },
  { key: 'voucher_payments',   name: 'Voucher Payments' },
  { key: 'salary_payments',    name: 'Salary Payments' },
  { key: 'expense_register',   name: 'Expense Register' },
  { key: 'bank_records',       name: 'Bank Records' },
  { key: 'inventory_items',    name: 'Inventory Items' },
  { key: 'contacts',           name: 'Contacts' },
];

const ReportDefinitionForm = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({
    key: '',
    name: '',
    description: '',
    allowed_formats: ['csv', 'xlsx'],
  });
  const [filters, setFilters] = useState([
    { label: 'Start Date', type: 'date' },
    { label: 'End Date', type: 'date' },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleTemplateSelect = (e) => {
    const selected = REPORT_TEMPLATES.find(t => t.key === e.target.value);
    setForm(prev => ({ ...prev, key: selected?.key || '', name: selected?.name || '' }));
  };

  const toggleFormat = (fmt) => {
    setForm(prev => ({
      ...prev,
      allowed_formats: prev.allowed_formats.includes(fmt)
        ? prev.allowed_formats.filter(f => f !== fmt)
        : [...prev.allowed_formats, fmt],
    }));
  };

  const addFilter = () => setFilters(prev => [...prev, { label: '', type: 'date' }]);
  const removeFilter = (idx) => setFilters(prev => prev.filter((_, i) => i !== idx));
  const updateFilter = (idx, key, value) =>
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [key]: value } : f));

  const buildInputSchema = () => {
    const properties = {};
    filters.forEach(({ label, type }) => {
      if (!label.trim()) return;
      const fieldKey = toKey(label);
      const meta = FILTER_TYPES.find(t => t.value === type);
      const prop = { type: meta.type };
      if (meta.format) prop.format = meta.format;
      properties[fieldKey] = prop;
    });
    return { properties };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.key) { setError('Please select a report type.'); return; }
    if (form.allowed_formats.length === 0) { setError('Select at least one download format.'); return; }

    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        API_ENDPOINTS.REPORTS_DEFINITIONS_LIST,
        { key: form.key, name: form.name, description: form.description, allowed_formats: form.allowed_formats, input_schema: buildInputSchema() },
        { headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      setSuccess(true);
      setTimeout(() => onSuccess(), 2000);
    } catch (err) {
      const data = err?.response?.data;
      setError(typeof data === 'object' ? Object.values(data).flat().join(' ') : data || err.message || 'Failed to create report.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="FileText" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Create Report Template</h2>
               
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Icon name="CheckCircle" size={36} className="text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">Template Created Successfully!</h3>
            <p className="text-sm text-muted-foreground text-center">
              <span className="font-medium text-foreground">{form.name}</span> template has been created.<br />
              Redirecting to templates list...
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`p-6 space-y-6 ${success ? 'hidden' : ''}`}>
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive flex items-center gap-2">
              <Icon name="AlertCircle" size={16} />
              {error}
            </div>
          )}

          {/* Section 1 — Basic Info */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">1</span>
              Basic Information
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                Report Type <span className="text-destructive">*</span>
              </label>
              <select
                value={form.key}
                onChange={handleTemplateSelect}
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              >
                <option value="">Select a report type...</option>
                {REPORT_TEMPLATES.map(t => (
                  <option key={t.key} value={t.key}>{t.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Choose the type of data this report will generate</p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={2}
                placeholder="What does this report show? e.g. Shows all sales invoices within a date range"
                className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
              />
            </div>
          </div>

          {/* Section 2 — Download Formats */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">2</span>
              Download Formats
              <span className="text-xs font-normal text-muted-foreground ml-1">— How can users download this report?</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {FORMAT_OPTIONS.map(({ value, label, desc }) => {
                const checked = form.allowed_formats.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleFormat(value)}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      checked
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/40'
                    }`}
                  >
                    <div className={`text-sm font-semibold ${checked ? 'text-primary' : 'text-foreground'}`}>{label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section 3 — Report Filters */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">3</span>
                Report Filters
                <span className="text-xs font-normal text-muted-foreground ml-1">— What can users filter by when running this template?</span>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addFilter} iconName="Plus" iconPosition="left">
                Add Filter
              </Button>
            </div>

            {filters.length === 0 ? (
              <div className="text-center py-6 border-2 border-dashed border-border rounded-lg">
                <Icon name="SlidersHorizontal" size={28} className="mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground">No filters added.</p>
                <p className="text-xs text-muted-foreground mt-1">Filters let users narrow down the report data, e.g. by date range or status.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_160px_24px] gap-2 px-3">
                  <span className="text-xs text-muted-foreground font-medium">Filter Label</span>
                  <span className="text-xs text-muted-foreground font-medium">Filter Type</span>
                  <span />
                </div>

                {filters.map((filter, idx) => {
                  const meta = FILTER_TYPES.find(t => t.value === filter.type);
                  return (
                    <div key={idx} className="grid grid-cols-[1fr_160px_24px] gap-2 items-center p-3 bg-muted/40 rounded-lg">
                      <div className="space-y-0.5">
                        <input
                          value={filter.label}
                          onChange={e => updateFilter(idx, 'label', e.target.value)}
                          placeholder="e.g. Start Date"
                          className="w-full px-2 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                        />
                        {meta && <p className="text-xs text-muted-foreground pl-1">{meta.example}</p>}
                      </div>

                      <div className="flex flex-col gap-1">
                        <select
                          value={filter.type}
                          onChange={e => updateFilter(idx, 'type', e.target.value)}
                          className="w-full px-2 py-1.5 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-background"
                        >
                          {FILTER_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        {meta && (
                          <div className="flex items-center gap-1 pl-1">
                            <Icon name={meta.icon} size={11} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{meta.label} input</span>
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFilter(idx)}
                        className="text-muted-foreground hover:text-destructive self-start mt-1.5"
                      >
                        <Icon name="Trash2" size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading} iconName={isLoading ? undefined : 'Save'} iconPosition="left">
              {isLoading ? 'Creating...' : 'Create Template'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportDefinitionForm;
